import { RecurrenceUnit, Schedule } from "@common/types/interface";
import moment, { Moment } from "moment";
import "moment-timezone";
import { ByWeekday, Frequency, Options, RRule, RRuleSet, rrulestr, Weekday, WeekdayStr } from "rrule";
import { convertRRuleToUIWeekdays, getSortedWeekdayButtons } from '../../../bt-common/utils/weekday-conversion';
import { formatInLanguage, getMomentLocale } from '@common/config/moment-locales';
import { formatClockTime, type ClockPreference } from '@common/config/time-format';
import { recurrenceCadenceSentence } from '@common/i18n/recurrence-cadence';
import { SCHEDULE_PAST_GRACE_MS } from '@common/scheduling/past-grace';
import { planRecurrenceCommit, type RecurrenceCommitPlan } from './recurrence-commit-plan';
import {
  tzidOfRuleString,
  instantToFloating,
  floatingIsoToTrueIso,
  toFloatingRuleString,
  addTzidToRuleString,
  untilFloatingToTrue,
} from '@common-services/utils/tzid-rule-frame';
import { canonicalizeIanaTimezone, getBrowserTimezone } from '@common/utils/timezone-capture';

export type CherryPickOptions = Pick<Options, "freq" | "dtstart" | "interval" | "wkst" | "count" | "until" | "byweekday" | "bymonthday">;
type CherryPickKeys = keyof CherryPickOptions;
export type WeekStartDay = 0 | 1; // Only allow Sunday (0) or Monday (1)
const allowedKeys: CherryPickKeys[] = ["freq", "dtstart", "interval", "wkst", "count", "until", "byweekday", "bymonthday"];

// startDayAtWeek: 0 = Sunday, 1 = Monday





const weekdaystr = (m: moment.Moment) => Weekday.fromStr(
  m.locale('en').format("dd").toUpperCase() as WeekdayStr
)

export const weekStart = weekdaystr(moment().day(0));
export const currentDay = weekdaystr(moment());

export const extractSignificantProps = (rule: RRule) => {
  return {
    freq: rule.origOptions.freq,
    bymonthday: Array.isArray(rule.options.bymonthday)
      ? rule.options.bymonthday
      : [rule.options.bymonthday].filter(Boolean),
    byweekday: Array.isArray(rule.options.byweekday)
      ? rule.options.byweekday
      : [rule.options.byweekday].filter(Boolean),
  };
};

export function getByWeekDay(dueDate: moment.Moment) {
  const dayOfWeek = dueDate.day();  // 0 (Sunday) to 6 (Saturday)
  const nthWeek = Math.ceil(dueDate.date() / 7);  // Which occurrence of the day in the month

  switch (dayOfWeek) {
    case 0: return RRule.SU.nth(nthWeek);
    case 1: return RRule.MO.nth(nthWeek);
    case 2: return RRule.TU.nth(nthWeek);
    case 3: return RRule.WE.nth(nthWeek);
    case 4: return RRule.TH.nth(nthWeek);
    case 5: return RRule.FR.nth(nthWeek);
    case 6: return RRule.SA.nth(nthWeek);
    default: throw new Error("Invalid day of the week.");
  }
}

const cherryPick = (rule: RRule) => Object.keys(rule.origOptions).filter(key => allowedKeys.includes(key as CherryPickKeys)).reduce((obj, key) => {
  const k = key as CherryPickKeys;
  (obj as Record<string, unknown>)[k] = (rule.origOptions as Record<string, unknown>)[k];
  return obj;
}, {} as CherryPickOptions);



export const ruleOptionsFromString = (rfcString: string): CherryPickOptions => cherryPick(RRule.fromString(rfcString))

export const defaultRuleOptions = (freq = RRule.WEEKLY, interval = 1) => ({
  freq: freq,
  interval: interval,
  byweekday: [currentDay.weekday],
  wkst: weekStart,
} as CherryPickOptions)

export const getWeekDay = (m: moment.Moment) => [Weekday.fromStr(
  m.locale('en').format("dd").toUpperCase() as WeekdayStr
).weekday]

export const initialRuleOptions = (m: moment.Moment) =>
  ({ ...defaultRuleOptions(), byweekday: getWeekDay(m) } as Partial<Options>)


// Safely derive rule options from rruleset; falls back to sensible defaults.
//
// FE#5.9.28: when there is no rule yet, the DEFAULT weekly BYDAY (and any day-of-month
// default) must be derived from the LOCAL calendar day in the create-time `timezone`
// — the same zone the rule is stamped with (the browser zone, via `resolveStampingTzid`).
// Deriving it from `moment.utc(dueDate)` gave the UTC weekday, which a TZID rule then
// reads as LOCAL → the wrong default day for any viewer whose local day ≠ the UTC day
// (east of UTC in the evening, west in the early morning). With no `timezone` the
// legacy UTC frame is used, byte-identical to before.
export const getRuleOptionsSafe = (
  rruleset: string | undefined,
  dueDate: string | moment.Moment,
  timezone?: string,
): Partial<Options> => {
  const dueMoment = moment.isMoment(dueDate)
    ? dueDate
    : (timezone ? moment.tz(dueDate, timezone) : moment.utc(dueDate));
  if (rruleset) {
    try {
      const rrule = fetchRule(rruleset);
      return rrule.origOptions;
    } catch (error) {
      console.error('Failed to parse rruleset in getRuleOptionsSafe:', rruleset, error);
    }
  }
  return initialRuleOptions(dueMoment);
}

/**
 * BE#648 — the time base a schedule-picker build site stamps onto the rrule it writes.
 *
 * The root-cause write path stamped DTSTART + BYHOUR in UTC with NO TZID, so the
 * shared engine had no zone to hold the wall-clock against and a series drifted an
 * hour across DST (a 10:10-Israel message became 09:10 after the autumn fall-back).
 * A series born from the picker must instead carry the create-time IANA zone and its
 * LOCAL wall-clock. The create-time zone is `effectiveTz` (FE#5.9.28): the rule's OWN
 * embedded TZID on an EDIT, else the browser-zone-first create default (browser zone →
 * workspace tz → legacy; see `resolveStampingTzid`). So:
 *
 *  - `byhour`/`byminute`: the time-of-day in that zone (10:10 Israel), not the UTC
 *    hour (07);
 *  - `dtstart`: a FLOATING Date whose UTC fields ARE that wall-clock — the exact
 *    shape rrule serialises as `DTSTART;TZID=<zone>:<YYYYMMDDTHHmmss>` and the shape
 *    the engine's `toFloatingRule` expects back;
 *  - `tzid`: that same zone.
 *
 * `moment.tz(dueDate, zone)` and `Date.UTC` are process-TZ-INDEPENDENT, so the
 * written artefact is identical whatever the browser's own clock.
 */
export interface RuleTimeBase {
  /** The create-time IANA zone (rule's own TZID on edit, else browser-zone-first
   *  default), or undefined for the legacy (UTC, no-TZID) write. */
  tzid?: string;
  byhour: number;
  byminute: number;
  dtstart: Date;
}

/**
 * Derive the {@link RuleTimeBase} for a due date and (optional) create-time timezone
 * (the rule's own TZID on an edit, else the browser-zone-first create default).
 *
 * With a `timezone`: wall-clock time-of-day + a floating DTSTART + the zone. Without
 * one (no resolvable zone): the LEGACY UTC write — byte-identical to the pre-BE#648
 * behaviour — so a series that cannot be zoned never regresses.
 */
export const ruleTimeBaseFor = (dueDate: string | Date, timezone?: string): RuleTimeBase => {
  if (!timezone) {
    const m = moment.utc(dueDate).startOf('minute');
    return { tzid: undefined, byhour: m.hour(), byminute: m.minute(), dtstart: m.toDate() };
  }
  const wall = moment.tz(dueDate, timezone);
  // A FLOATING Date: its UTC fields carry the create-time wall-clock, so rrule writes
  // `DTSTART;TZID=<zone>:<wall-clock>` and the engine reads the same wall-clock back.
  const dtstart = new Date(Date.UTC(wall.year(), wall.month(), wall.date(), wall.hour(), wall.minute(), 0));
  return { tzid: timezone, byhour: wall.hour(), byminute: wall.minute(), dtstart };
};

/**
 * Stamp a {@link RuleTimeBase} onto an rrule option set: always the DTSTART and the
 * TZID (null on the legacy path); the wall-clock BYHOUR/BYMINUTE only where the rule
 * ALREADY constrains them (MONTHLY/WEEKLY/YEARLY). Sub-daily frequencies take their
 * time-of-day from DTSTART and are deliberately left untouched — forcing a BYHOUR on
 * them would change the schedule's meaning.
 */
export const stampRuleTimeBase = (options: Partial<Options>, base: RuleTimeBase): Partial<Options> => {
  const stamped: Partial<Options> = { ...options, dtstart: base.dtstart, tzid: base.tzid ?? null };
  if (Array.isArray(options.byhour) && options.byhour.length > 0) stamped.byhour = [base.byhour];
  if (Array.isArray(options.byminute) && options.byminute.length > 0) stamped.byminute = [base.byminute];
  return stamped;
};

/**
 * FE#5.9.28 — the TZID a NEW recurring rule (one that carries no embedded TZID yet)
 * is born with. #648/5.9.27 defaulted this to the WORKSPACE zone; we now prefer the
 * VIEWER's own BROWSER zone so a user scheduling from a zone that differs from the
 * workspace's gets THEIR wall-clock. Fallback chain:
 *
 *   browser zone (canonicalised) → workspace tz (canonicalised) → legacy (undefined)
 *
 * `undefined` is the legacy UTC / no-TZID write (see {@link ruleTimeBaseFor}), so a
 * context with no resolvable zone never regresses. REUSES the #656 mirror helpers
 * (`canonicalizeIanaTimezone` rejects offsets / `Etc/*` / `UTC` / invalid and maps
 * aliases → canonical; `getBrowserTimezone` reads `Intl`) — pure + process-TZ-
 * independent: the result depends on the browser/workspace zone, never on the clock.
 */
export const createDefaultTzid = (workspaceTimezone?: string): string | undefined =>
  canonicalizeIanaTimezone(getBrowserTimezone()) ??
  canonicalizeIanaTimezone(workspaceTimezone) ??
  undefined;

/**
 * FE#5.9.28 — the single shared TZID source every schedule-picker build site stamps:
 *  - an EXISTING rule keeps its OWN embedded TZID (edits stay byte-identical — the
 *    rule's zone is authoritative, never re-derived from the browser/workspace);
 *  - a NEW rule (no TZID yet) uses {@link createDefaultTzid} (browser zone first).
 *
 * This replaces the per-surface `tzidOfRuleString(rruleset) ?? workspaceTimezone`
 * expression so compose, the scheduling wizard, drips, the calendar create flow and
 * the recurrence popover all resolve the create-time zone identically.
 */
export const resolveStampingTzid = (
  rruleset: string | undefined,
  workspaceTimezone?: string,
): string | undefined => tzidOfRuleString(rruleset) ?? createDefaultTzid(workspaceTimezone);

/**
 * BE#648 (B1) — rebuild a recurring rule after the TIME was edited, stamping the
 * WORKSPACE zone + LOCAL wall-clock for the new time.
 *
 * The time picker used to write `dtstart`/`BYHOUR` in UTC while keeping the series'
 * inherited TZID, so editing an IL 11:00 wrote `…T090000`/`BYHOUR=9` and every later
 * occurrence fired at 09:00 IL. This mirrors the date-picker/popover fix: DTSTART
 * (floating wall-clock) + TZID, and — only for the frequencies that constrain the
 * hour (WEEKLY/MONTHLY/YEARLY) — the wall-clock BYHOUR/BYMINUTE for the NEW time.
 * Sub-daily frequencies take their time from DTSTART. `tzid` is set explicitly (null
 * on the legacy path) so an inherited TZID can never survive onto a UTC write.
 */
export const buildTimeEditedRuleString = (
  ruleOptions: Partial<Options>,
  newDueDateIso: string,
  timezone?: string,
): string => {
  const base = ruleTimeBaseFor(newDueDateIso, timezone);
  const opts: Partial<Options> = { ...ruleOptions, dtstart: base.dtstart, tzid: base.tzid ?? null };
  if (
    ruleOptions.freq === Frequency.WEEKLY ||
    ruleOptions.freq === Frequency.MONTHLY ||
    ruleOptions.freq === Frequency.YEARLY
  ) {
    opts.byhour = base.byhour;
    opts.byminute = base.byminute;
    opts.bysecond = 0;
  }
  return new RRule(opts).toString();
};

/**
 * BE#648 (M2) — the end of the picked calendar day as a true UTC instant, computed
 * in the RULE's zone (not the browser's).
 *
 * An "until <day>" end is a true UTC instant equal to end-of-day in the series' zone
 * (the reading the shared engine uses). Computing `moment(date).endOf('day')` in the
 * browser zone is wrong for a cross-tz user — it can clip or extend the last send.
 * With no zone it falls back to browser-local end-of-day, byte-identical to before.
 */
export const endOfDayInZone = (date: Date | string | moment.Moment, timezone?: string): Date => {
  const m = moment(date);
  if (!timezone) return m.endOf('day').toDate();
  return moment.tz({ year: m.year(), month: m.month(), day: m.date() }, timezone).endOf('day').toDate();
};


export const toRuleSet = (rfcString: string) => {
  try {
    return rrulestr(rfcString, {
      forceset: true,
    }) as RRuleSet;
  } catch (error) {
    console.error('Failed to parse RRule string:', rfcString, error);
    // Return a default RRuleSet if parsing fails
    const defaultSet = new RRuleSet();
    defaultSet.rrule(new RRule(defaultRuleOptions()));
    return defaultSet;
  }
};

export const fetchRule = (rfcString: string): RRule => {
  try {
    const ruleSet = toRuleSet(rfcString);
    return ruleSet._rrule[0];
  } catch (error) {
    console.error('Failed to fetch rule from RRule string:', rfcString, error);
    // Return a default rule if fetching fails
    return new RRule(defaultRuleOptions());
  }
};

// Convert RRule weekdays to UI display values for ToggleButtonGroup
export const extractWeekdayNumbers = (weekdays: ByWeekday | ByWeekday[] | null | undefined, startDayAtWeek?: WeekStartDay): number[] => {
  if (weekdays === null || weekdays === undefined) {
    return [];
  }
  // If it's just a single value, wrap it in an array for consistent processing
  const arrayWeekdays = Array.isArray(weekdays) ? weekdays : [weekdays];
  
  // Convert RRule weekdays to numbers first
  const rruleNumbers = arrayWeekdays.map(day => {
    const weekdayNum = day instanceof Weekday ? day.weekday : day as number;
    return weekdayNum;
  });
  
  // Use shared utility to convert to UI values
  // Use the same default as getSortedWeekdayButtons (0 = Sunday-first)
  return convertRRuleToUIWeekdays(rruleNumbers, startDayAtWeek || 0);
};

// Helper function to determine the closest occurrence either the next or the previous
export function getClosestOccurrence(currentDate: Moment, nextOccurrence: Moment | null, prevOccurrence: Moment | null): moment.Moment | null {
  if (nextOccurrence && prevOccurrence) {
    const nextDiff = Math.abs(nextOccurrence.diff(currentDate));
    const prevDiff = Math.abs(prevOccurrence.diff(currentDate));
    return nextDiff < prevDiff ? nextOccurrence : prevOccurrence;
  } else if (nextOccurrence) {
    return nextOccurrence;
  } else if (prevOccurrence) {
    return prevOccurrence;
  }
  return null;
}

export const patchRuleSet = (set: RRuleSet, patch: Partial<CherryPickOptions>) => {
  const { _rrule } = set;
  const newSet = new RRuleSet();


  // Copy existing exdates to the new set
  set.exdates().forEach(x => newSet.exdate(x));

  const newStartDate = patch.dtstart ? new Date(patch.dtstart) : null;

  // Normalize until date to end of day if present, to ensure the last day is inclusive
  // regardless of the time of the event vs the time of the until date (which often defaults to 00:00)
  if (patch.until) {
      patch.until = moment(patch.until).endOf('day').toDate();
  }

  // Combine each original rule's options with the patch options
  _rrule.forEach(r => {
    const newOptions = {
      ...r.origOptions, // Start with the original rule's options
      ...patch          // Apply the patch options
    };

    if (newStartDate) {
      // Adjust the start date if provided in the patch
      newOptions.dtstart = newStartDate;
    }

    newSet.rrule(new RRule(newOptions)); // Create a new RRule with the combined options
  });

  return newSet;

  // const { dtstart, _rrule } = set;
  // const newSet = new RRuleSet();
  // newSet.dtstart = dtstart;
  // set.exdates().forEach(x => newSet.exdate(x));

  // _rrule.forEach(r => {
  //   newSet.rrule(new RRule({ ...ruleOptionsFromString(RRule.optionsToString(r.origOptions)), ...patch }))
  // })
  // return newSet
};


export const getRuleValue = (set: RRuleSet, key: CherryPickKeys) => set._rrule[0].origOptions[key];

export const parseOldUnits = (recurenceUnit: RecurrenceUnit, recurrenceQuantity: number): RRuleSet => {
  const ruleset = new RRuleSet();
  ruleset.rrule(
    new RRule(
      defaultRuleOptions(
        parseRecurrenceUnit(recurenceUnit),
        recurrenceQuantity
      )
    )
  );
  return ruleset;
}

export const getDefaultOptions = (recurenceUnit: RecurrenceUnit, recurrenceQuantity: number) =>
  defaultRuleOptions(parseRecurrenceUnit(recurenceUnit), recurrenceQuantity)

export const optionsFromRuleset = (schedule: Schedule) => {
  let options;
  if (!!schedule.rruleset) {
    try {
      const ruleRfc = toRuleSet(schedule.rruleset)._rrule[0].toString();
      options = ruleOptionsFromString(ruleRfc);

      const { byweekday, freq } = options;
      if (freq === Frequency.WEEKLY) {
        const bwd = (byweekday as Weekday[]).map((d) => d.weekday);
        options = { ...options, byweekday: bwd };
      }
    } catch (error) {
      console.error('Failed to parse rruleset in optionsFromRuleset:', schedule.rruleset, error);
      // Fall back to default options if parsing fails
      options = getDefaultOptions(
        schedule.recurenceUnit ?? 'day',
        schedule.recurrenceQuantity ?? 1
      );
    }
  } else {
    options = getDefaultOptions(
      schedule.recurenceUnit ?? 'day',
      schedule.recurrenceQuantity ?? 1
    )
  }
  return options;
}

export const parseRecurrenceUnit = (unit: RecurrenceUnit): Frequency => {
  switch (unit) {
    case "day":
      return Frequency.DAILY;
    case "hour":
      return Frequency.HOURLY;
    case "minute":
      return Frequency.MINUTELY;
    case "month":
      return Frequency.MONTHLY;
    case "week":
      return Frequency.WEEKLY;
    case "year":
      return Frequency.YEARLY;
    default:
      return Frequency.DAILY;
  }
};

export const weekOfMonth = (m: moment.Moment): number => {
  const { months } = m.toObject();
  if (months !== moment(m).add(7, "d").toObject().months) return -1
  if (months !== moment(m).subtract(7, "d").toObject().months) return 1
  if (months !== moment(m).subtract(14, "d").toObject().months) return 2
  if (months !== moment(m).subtract(21, "d").toObject().months) return 3
  if (months !== moment(m).subtract(28, "d").toObject().months) return 4
  return 5;
};


/**
 * The sentence the compose dialog's summary strip and the schedule picker both
 * show: "This message will be sent on …".
 *
 * DATES ARE WRITTEN OUT, NOT NUMBERED. This read "sent on 10/09/2026", which is
 * 10 September to half the world and 9 October to the other half — in the one
 * strip that exists specifically to remove ambiguity about what pressing Send
 * will do. Nothing here hand-rolls a date any more: `formatInLanguage` is the
 * app's own formatter and takes the render's language explicitly (moment's
 * GLOBAL locale is updated from i18next's languageChanged handler, which can
 * land AFTER a render — that is exactly how a Hebrew dialog ended up showing an
 * English date). 'LL' is the locale's own long date, 'LT' its own time, so
 * Hebrew gets "10 בספטמבר 2026" and a 24-hour clock rather than a transliterated
 * "10/09/2026 05:50 AM".
 *
 * `language` is REQUIRED rather than defaulted, so the compiler finds every call
 * site instead of silently formatting somebody's screen in English.
 */
export const generateRecurrenceText = (scheduleMessage: Schedule, ruleOptions: Partial<Options>, startDayAtWeek: WeekStartDay, t: (key: string, options?: any) => string, language: string | null | undefined, clockPreference?: ClockPreference): string => {
  const time = moment.utc(scheduleMessage.dueDate).local();
  const timeStr = formatClockTime(time, language, clockPreference);
  // Weekday included: "Thursday, 10 September 2026" answers "is that soon?"
  // without the reader counting, which a bare date never does.
  const dateStr = formatInLanguage(time, language, 'dddd, LL');
  let baseText = ''
  if (!scheduleMessage.isRecurring) {
    return t('messageScheduling.willBeSentOn', { date: dateStr, time: timeStr });
  }

  let weekdaysText = '';
  if (ruleOptions.byweekday) {
    const weekdays = Array.isArray(ruleOptions.byweekday)
      ? ruleOptions.byweekday
      : [ruleOptions.byweekday];

    // Sort weekdays in chronological order starting from startDayAtWeek
    const sortedWeekdays = [...weekdays].sort((a, b) => {
      // Get weekday numbers (0-6) from RRule: Monday=0, Tuesday=1, Wednesday=2, Thursday=3, Friday=4, Saturday=5, Sunday=6
      let aNum = (a as Weekday).weekday;
      let bNum = (b as Weekday).weekday;

      // Convert RRule weekday numbers to display order based on startDayAtWeek
      const getDisplayOrder = (weekdayNum: number): number => {
        if (startDayAtWeek === 0) {
          // Sunday first: Sunday=0, Monday=1, Tuesday=2, Wednesday=3, Thursday=4, Friday=5, Saturday=6
          return weekdayNum === 6 ? 0 : weekdayNum + 1;
        } else {
          // Monday first: Monday=0, Tuesday=1, Wednesday=2, Thursday=3, Friday=4, Saturday=5, Sunday=6
          return weekdayNum;
        }
      };

      return getDisplayOrder(aNum) - getDisplayOrder(bNum);
    });

    const weekdayNames = sortedWeekdays.map(day => getWeekdayName(day as Weekday, startDayAtWeek, t));

    if (weekdayNames.length > 1) {
      const lastDay = weekdayNames.pop();
      weekdaysText = `${weekdayNames.join(', ')} ${t('common.and', 'and')} ${lastDay}`;
    } else {
      weekdaysText = weekdayNames[0];
    }
  }

  // Read the day off the MOMENT, not off a formatted string — dateStr is a
  // written-out localized date now and splitting it on '/' would have silently
  // produced NaN.
  // "the 24th of every month" in English; the locale's own ordinal elsewhere
  // (Hebrew has none, and moment correctly renders the bare number).
  const ordinal = formatInLanguage(time, language, 'Do');
  // Day-and-month with no year, in the locale's own order — the hardcoded
  // English month table this used to walk was unreadable in every other language.
  const dateWithoutYear = formatInLanguage(time, language, 'MMMM D');

  // ── INTERVAL ──────────────────────────────────────────────────────────────
  // Everything below this point describes a frequency and never once mentioned
  // how OFTEN. A rule of "every 2 weeks on Monday" previewed as "This message
  // will be sent every Monday at 12:00" — a WEEKLY schedule shown to someone
  // about to commit a fortnightly one, in the sentence that exists to tell them
  // what they are committing.
  //
  // Rather than grow six more frames, an interval greater than 1 hands the whole
  // cadence clause to the shared grammar the calendar hover card and the
  // campaigns summary already use, so all three say "every 2 weeks on Monday" in
  // the same words. The single-interval frames are kept: they are richer (the
  // monthly one names the ordinal, "the 24th of each month") and they were never
  // wrong. If the rule is one the grammar declines to describe — HOURLY,
  // MINUTELY — it returns '' and we fall through to the existing frame rather
  // than print half a sentence.
  const interval = ruleOptions.interval ?? 1;
  const cadence = interval > 1
    ? recurrenceCadenceSentence(
      ruleOptions,
      // The render's OWN language, not moment's global locale — the global is
      // updated from an i18next event that can land after this render, which is
      // how a Hebrew dialog once ended up with English day names.
      moment().locale(getMomentLocale(language)).localeData().weekdays(),
      t,
    )
    : '';

  if (cadence) {
    baseText = t('messageScheduling.willBeSentCadence', { cadence, time: timeStr });
  } else if (ruleOptions.freq === Frequency.WEEKLY) {
    baseText = t('messageScheduling.willBeSentEveryWeekday', { weekdays: weekdaysText, time: timeStr });
  } else if (ruleOptions.freq === Frequency.MONTHLY) {
    // "The message will be sent on the 24th of every month at 12:00."
    baseText = t('messageScheduling.willBeSentMonthly', { ordinal, time: timeStr });
  } else if (ruleOptions.freq === Frequency.YEARLY) {
    baseText = t('messageScheduling.willBeSentYearly', { date: dateWithoutYear, time: timeStr });
  } else if (ruleOptions.freq === Frequency.HOURLY) {
    baseText = t('messageScheduling.willBeSentHourly', { date: dateStr, time: timeStr });
  } else if (ruleOptions.freq === Frequency.MINUTELY) {
    baseText = t('messageScheduling.willBeSentMinutely', { date: dateStr, time: timeStr });
  } else if (ruleOptions.freq === Frequency.DAILY) {
    baseText = t('messageScheduling.willBeSentDaily', { date: dateStr, time: timeStr });
  }

  if (ruleOptions.until) {
    const untilDate = formatInLanguage(ruleOptions.until, language, 'LL');
    return `${baseText} ${t('messageScheduling.until', { date: untilDate })}`;
  }

  if (ruleOptions.count) {
    // Was `for ${count} times` — RFC 5545's own vocabulary (COUNT), assembled in
    // code with an English plural rule, appended to a sentence that had just gone
    // to the trouble of being localized. It read "…at 12:00 for 3 times".
    return `${baseText} ${t('messageScheduling.forOccurrences', { count: ruleOptions.count })}`;
  }

  return baseText;
};


const getWeekdayName = (weekday: Weekday, startDayAtWeek: WeekStartDay, t: (key: string, options?: any) => string): string => {
  // RRule weekday numbers: Monday=0, Tuesday=1, Wednesday=2, Thursday=3, Friday=4, Saturday=5, Sunday=6
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayNumber = weekday.weekday;
  return t(`weekdays.${dayKeys[dayNumber]}`);
};

// formatDateWithoutYear / formatTime / getOrdinalSuffix lived here and are
// gone: a hardcoded English month table, an `hh:mm A` clock and an English
// ordinal rule ("21st"), all applied regardless of the reader's language, and
// all three replaced by formatInLanguage above. They had no other callers.




export const getWeekdayOrder = (weekday: number, startDayAtWeek: WeekStartDay): number => {
  // Convert RRULE weekday to UI weekday using shared utility
  const uiWeekdays = convertRRuleToUIWeekdays([weekday], startDayAtWeek);
  return uiWeekdays[0];
};

/**
 * Finds the closest valid occurrence for a recurrence pattern from a given date.
 * If the given date is valid, returns it. Otherwise, finds the next or previous valid occurrence.
 *
 * A candidate that has ALREADY PASSED is not a candidate.
 *
 * The dialog stores what comes back here as both the message's dueDate and the
 * rule's DTSTART, so an answer behind the clock is a message silently re-dated into
 * the past — observed twice in the real UI, a start of tomorrow becoming yesterday,
 * with the message still sendable. Choosing purely on `Math.abs(diff)`, as this did,
 * makes that the NORMAL outcome whenever the chosen start sits just after an
 * occurrence: the one behind is nearer than the one ahead roughly half the time.
 *
 * Moving BACKWARDS is not the defect and is not blocked: a Friday start on a
 * Wednesdays pattern belongs on the Wednesday two days back, not the one five days
 * on, and the copy that will announce this move must be free to say so. The bound is
 * only that the result is still ahead of the clock.
 *
 * The bound is applied to the instant this function will actually RETURN — the
 * candidate day carrying `currentDate`'s time of day — not to the raw instant the
 * rule emits. The two differ and the difference has the dangerous sign: a rule
 * instant later today, re-timed to this morning, is a past dueDate that a check on
 * the rule instant waves through.
 *
 * @param currentDate - The current date to check (moment object in UTC)
 * @param rruleset - The RRule string defining the recurrence pattern
 * @param now - The clock to judge "already passed" against; defaults to the real one.
 *              A parameter so the rule is testable, NOT so it is optional in effect —
 *              the production call site passes nothing and must still be guarded.
 * @returns The closest valid occurrence that has not passed, as a moment object in
 *          UTC, or null when the pattern has none — in which case the caller must
 *          leave the customer's date alone rather than invent one.
 */
/**
 * How far forward the search for a not-yet-passed occurrence will walk before giving
 * up and reporting none. Most walks are WEEKLY/MONTHLY/YEARLY, where this is roughly a
 * decade of weekly steps. A DAILY rule reaches the walk only with a start that has
 * already passed (every daily date fits, so a future one returns early), and there it
 * is about a year and a half of days. Giving up returns null, and null makes the
 * caller leave the customer's date alone, which is the safe direction to fail in.
 */
const MAX_FORWARD_OCCURRENCE_STEPS = 520;

export const findClosestValidOccurrence = (
  currentDate: moment.Moment,
  rruleset: string,
  now: moment.Moment = moment.utc()
): moment.Moment | null => {
  try {
    const rule = fetchRule(rruleset);
    const currentDateObj = currentDate.toDate();

    // A start that already fits the pattern is kept as the customer chose it —
    // but only while it is still ahead of the clock. Fitting is not an exemption
    // from the past check: under FREQ=DAILY every date fits, so without this bound
    // the guard below would be unconditionally off for every daily rule. A past
    // start that fits falls through to the forward walk like any other.
    if (
      isDateValidForRecurrence(currentDateObj, rruleset, currentDate.toISOString())
      && !currentDate.isBefore(now)
    ) {
      return currentDate;
    }

    // Re-time a rule instant onto the start the customer chose. This is the value
    // that would be stored, so it is the value the "not in the past" bound applies to.
    const asStoredStart = (occurrence: Date): moment.Moment =>
      moment.utc(occurrence)
        .hour(currentDate.hour())
        .minute(currentDate.minute())
        .second(currentDate.second())
        .millisecond(0);

    // Forward: WALK, do not peek. When the chosen start is itself behind the clock —
    // an overdue or long-untouched message — the single next occurrence after it can
    // still be in the past while a perfectly good one sits a few steps further on.
    // Peeking once and rejecting it would answer "no valid start exists" to a pattern
    // that plainly has one.
    let nextValid: moment.Moment | null = null;
    let cursor: Date | null = currentDateObj;
    for (let step = 0; step < MAX_FORWARD_OCCURRENCE_STEPS && cursor; step++) {
      const occurrence: Date | null = rule.after(cursor, false);
      if (!occurrence) break;
      const stored = asStoredStart(occurrence);
      if (stored.isSameOrAfter(now)) {
        nextValid = stored;
        break;
      }
      cursor = occurrence;
    }

    // Backward: one step is the whole search. Every further step is further into the
    // past, so if the occurrence immediately before the chosen start has passed, so
    // has every occurrence behind it.
    const prevOccurrence = rule.before(currentDateObj, false);
    const prevStored = prevOccurrence ? asStoredStart(prevOccurrence) : null;
    const prevValid = prevStored && prevStored.isSameOrAfter(now) ? prevStored : null;

    // Then, and only then, nearest-wins. Discarding first and choosing second is what
    // makes the bound a property of the answer rather than a veto on it: with the
    // nearer candidate in the past the further one is returned, not nothing.
    return getClosestOccurrence(currentDate, nextValid, prevValid);
  } catch (error) {
    console.error('Failed to find closest valid occurrence:', error);
    return null;
  }
};

/**
 * What committing the recurrence popover will do — the ONE function the popover
 * calls, for the notice it shows before Done and for the write Done makes.
 *
 * WHY THIS EXISTS AS A FUNCTION AND NOT AS CODE IN THE POPOVER
 * `726845379` fixed `findClosestValidOccurrence` so that a past start which FITS
 * the pattern walks forward, and tested it. The popover then went on calling that
 * function only when the start did NOT fit (`closestValid = isValid ? null : …`),
 * so the fixed path was unreachable from the product and a daily message could
 * still be committed to start yesterday. Deciding here, and testing THIS, closes
 * the gap between the function that is tested and the one that runs.
 *
 * THE RULE: a recurring message cannot START in the past. When the chosen start
 * has passed — fitting the pattern or not — the series begins at its first
 * occurrence that has not, and the caller is told why (`moveReason`), so it can
 * say so before committing. A start that is still ahead and fits is left exactly
 * as chosen. A start still ahead that does not fit moves to the nearest date that
 * does (which may be EARLIER than the chosen one — never call it "next").
 *
 * THE EDGE: "passed" means older than the shared one-minute grace
 * (`SCHEDULE_PAST_GRACE_MS`), the same boundary the compose dialog's Send gate
 * uses. The helper is handed that floor as its clock, so a candidate inside the
 * grace counts as "now" on both sides: Done never leaves behind a start the gate
 * refuses, and the gate never refuses a start Done would have kept.
 *
 * When nothing ahead fits (an exhausted COUNT/UNTIL), the start is left alone and
 * the Send gate — not this function — is what refuses it.
 */
export const resolveRecurrenceCommit = (input: {
  /** The start the customer chose (the draft's dueDate), as a UTC moment. */
  chosenStart: moment.Moment;
  /** The rule as configured, anchored at `chosenStart`. */
  rruleset: string;
  /** The draft's dueDate as stored — carries the time of day for monthly checks. */
  dueDate: string | undefined;
  /** The clock. Defaults to the real one; a parameter so the rule is testable. */
  now?: moment.Moment;
}): RecurrenceCommitPlan => {
  // BE#648: a MIGRATED (TZID) series re-anchors in the FLOATING frame so the rebuilt
  // rule keeps DTSTART;TZID + local-wall BYHOUR (not a browser-shifted true-instant
  // DTSTART) and the stored dueDate is the true instant. Legacy series: unchanged.
  const commitTzid = tzidOfRuleString(input.rruleset);
  if (commitTzid) return resolveRecurrenceCommitInTz(input, commitTzid);

  const { chosenStart, rruleset, dueDate } = input;
  const floor = (input.now ?? moment.utc()).clone().subtract(SCHEDULE_PAST_GRACE_MS, 'milliseconds');

  const startIsValid = isDateValidForRecurrence(chosenStart.toDate(), rruleset, dueDate);
  const startHasPassed = chosenStart.isBefore(floor);

  // Ask the helper whenever the start is not already acceptable — off-pattern OR
  // passed. This condition is the whole fix; `isValid ? null : …` was the hole.
  const closestValid = startIsValid && !startHasPassed
    ? null
    : findClosestValidOccurrence(chosenStart, rruleset, floor);

  // `startOf('minute')` applied ONCE, to a value both outputs then read, so the
  // stored dueDate and the rule's DTSTART agree by construction.
  const anchoredStart = closestValid ? closestValid.clone().startOf('minute') : null;
  const reAnchored = anchoredStart
    ? new RRule({
      ...fetchRule(rruleset).origOptions,
      dtstart: anchoredStart.toDate(),
    }).toString()
    : null;

  return planRecurrenceCommit({
    rruleset,
    startIsValid,
    startHasPassed,
    closestValidStart: anchoredStart ? anchoredStart.toISOString() : null,
    rrulesetAtClosestStart: reAnchored,
  });
};

/**
 * {@link resolveRecurrenceCommit} for a MIGRATED (TZID) series.
 *
 * Runs the SAME decision in the FLOATING frame (rule + chosenStart + now normalised
 * to wall-clock, so `findClosestValidOccurrence`/`isDateValidForRecurrence` are
 * deterministic and runtime-TZ-independent), then denormalises: the re-anchored rule
 * is rebuilt FLOATING and re-stamped with its TZID + local-wall BYHOUR, and the moved
 * dueDate is returned as the TRUE instant. The no-move case keeps the original TZID
 * rule as-is (already born-correct).
 */
const resolveRecurrenceCommitInTz = (input: {
  chosenStart: moment.Moment;
  rruleset: string;
  dueDate: string | undefined;
  now?: moment.Moment;
}, tzid: string): RecurrenceCommitPlan => {
  const { chosenStart, rruleset, dueDate } = input;
  const floatingRuleset = toFloatingRuleString(rruleset);
  const chosenFloat = moment.utc(instantToFloating(chosenStart.toDate(), tzid));
  const nowFloat = moment.utc(instantToFloating((input.now ?? moment.utc()).toDate(), tzid));
  const floor = nowFloat.clone().subtract(SCHEDULE_PAST_GRACE_MS, 'milliseconds');
  const dueFloat = dueDate ? instantToFloating(new Date(dueDate), tzid).toISOString() : undefined;

  const startIsValid = isDateValidForRecurrence(chosenFloat.toDate(), floatingRuleset, dueFloat);
  const startHasPassed = chosenFloat.isBefore(floor);

  const closestValid = startIsValid && !startHasPassed
    ? null
    : findClosestValidOccurrence(chosenFloat, floatingRuleset, floor);

  const anchoredStart = closestValid ? closestValid.clone().startOf('minute') : null;
  const reAnchoredFloating = anchoredStart
    ? new RRule({
      ...fetchRule(floatingRuleset).origOptions,
      dtstart: anchoredStart.toDate(),
    }).toString()
    : null;

  return planRecurrenceCommit({
    // No-move: the original TZID rule is already correct, store it unchanged.
    rruleset,
    startIsValid,
    startHasPassed,
    closestValidStart: anchoredStart ? floatingIsoToTrueIso(anchoredStart.toISOString(), tzid) : null,
    // Re-stamp the TZID AND denormalise the (floatified, see toFloatingRuleString) UNTIL
    // back to a true UTC instant, so an existing end date is preserved exactly.
    rrulesetAtClosestStart: reAnchoredFloating
      ? untilFloatingToTrue(addTzidToRuleString(reAnchoredFloating, tzid), tzid) ?? null
      : null,
  });
};

/**
 * Checks if a date matches the recurrence pattern defined by an RRule.
 * This is used to disable dates in the date picker that don't conform to the recurrence.
 * 
 * @param date - The date to check (only date part is considered, time is ignored)
 * @param rruleset - The RRule string defining the recurrence pattern
 * @param dueDate - The current due date (used to get time component for validation)
 * @returns true if the date matches the recurrence pattern, false otherwise
 */
export const isDateValidForRecurrence = (
  date: Date,
  rruleset: string | undefined,
  dueDate: string | undefined
): boolean => {
  // If no recurrence is set, all dates are valid
  if (!rruleset || !dueDate) {
    return true;
  }

  try {
    const rule = fetchRule(rruleset);
    const options = rule.origOptions;
    const dateMoment = moment(date);
    
    // For DAILY, HOURLY, and MINUTELY frequencies, all dates are valid
    if (options.freq === Frequency.DAILY || 
        options.freq === Frequency.HOURLY || 
        options.freq === Frequency.MINUTELY) {
      return true;
    }

    // For WEEKLY frequency, check if the date's weekday matches byweekday
    if (options.freq === Frequency.WEEKLY) {
      if (!options.byweekday) {
        return true; // No weekday restriction, all dates valid
      }
      
      const weekdays = Array.isArray(options.byweekday) 
        ? options.byweekday 
        : [options.byweekday];
      
      // Get the weekday number from the date (0=Monday, 6=Sunday in RRule)
      const dateWeekday = dateMoment.day(); // 0=Sunday, 6=Saturday in moment
      // Convert moment weekday to RRule weekday (moment: 0=Sun, RRule: 6=Sun)
      const rruleWeekday = dateWeekday === 0 ? 6 : dateWeekday - 1;
      
      // Check if the date's weekday is in the allowed weekdays
      return weekdays.some(wd => {
        const weekdayNum = wd instanceof Weekday ? wd.weekday : wd as number;
        return weekdayNum === rruleWeekday;
      });
    }

    // For MONTHLY frequency, check if the date's day of month matches bymonthday
    if (options.freq === Frequency.MONTHLY) {
      if (options.byweekday) {
        // If byweekday is specified, we need to check if this date falls on the correct weekday
        // and in the correct week of the month (e.g., first Monday, second Tuesday)
        const weekdays = Array.isArray(options.byweekday) 
          ? options.byweekday 
          : [options.byweekday];
        
        const dateWeekday = dateMoment.day();
        const rruleWeekday = dateWeekday === 0 ? 6 : dateWeekday - 1;
        
        // Check if weekday matches
        const weekdayMatches = weekdays.some(wd => {
          const weekdayNum = wd instanceof Weekday ? wd.weekday : wd as number;
          return weekdayNum === rruleWeekday;
        });
        
        if (!weekdayMatches) {
          return false;
        }
        
        // To check if it's the correct occurrence (e.g., first Monday vs second Monday),
        // we use the original rule and check if the test date appears as an occurrence
        // in the same month
        const dueMoment = moment.utc(dueDate);
        const testDate = moment(date)
          .hour(dueMoment.local().hour())
          .minute(dueMoment.local().minute())
          .second(0)
          .millisecond(0);
        
        // Use the original rule's dtstart to maintain the pattern
        const originalDtstart = options.dtstart ? moment(options.dtstart) : dueMoment;
        const testRule = new RRule({
          ...options,
          dtstart: originalDtstart.toDate(),
        });
        
        // Get occurrences in the same month as the test date
        const monthStart = testDate.clone().startOf('month');
        const monthEnd = testDate.clone().endOf('month');
        const occurrences = testRule.between(monthStart.toDate(), monthEnd.toDate(), true);
        
        // Check if the test date (day part) matches any occurrence
        return occurrences.some(occ => {
          const occMoment = moment(occ);
          return occMoment.isSame(testDate, 'day');
        });
      } else if (options.bymonthday) {
        // Check if the day of month matches
        const monthdays = Array.isArray(options.bymonthday) 
          ? options.bymonthday 
          : [options.bymonthday];
        const dayOfMonth = dateMoment.date();
        return monthdays.includes(dayOfMonth);
      } else {
        // No restrictions, all dates valid
        return true;
      }
    }

    // For YEARLY frequency, check if month and day match
    if (options.freq === Frequency.YEARLY) {
      const dueMoment = moment.utc(dueDate);
      const testDate = moment(date)
        .hour(dueMoment.local().hour())
        .minute(dueMoment.local().minute())
        .second(0)
        .millisecond(0);
      
      // Use the original rule's dtstart to maintain the pattern
      const originalDtstart = options.dtstart ? moment(options.dtstart) : dueMoment;
      const testRule = new RRule({
        ...options,
        dtstart: originalDtstart.toDate(),
      });
      
      // Get occurrences in the same year as the test date
      const yearStart = testDate.clone().startOf('year');
      const yearEnd = testDate.clone().endOf('year');
      const occurrences = testRule.between(yearStart.toDate(), yearEnd.toDate(), true);
      
      // Check if the test date (month and day) matches any occurrence
      return occurrences.some(occ => {
        const occMoment = moment(occ);
        return occMoment.month() === testDate.month() && occMoment.date() === testDate.date();
      });
    }

    // For other frequencies or unknown patterns, allow the date
    return true;
  } catch (error) {
    console.error('Failed to validate date against recurrence:', error);
    // On error, allow the date to avoid blocking the user
    return true;
  }
};
